import * as THREE from 'three';
import { animate, setAnimation, updateSpotlightTarget } from './Animate';
//import GUI from 'three/examples/jsm/libs/lil-gui.module.min.js';
import { cameraControls, fitSpotlightToCamera, getActiveCamera, loadMainScene, mainCameraInitPosition, mainCameraInitRotation, renderer, scene } from './Initial3DSetup';
import { loadCelestialBodies, planets } from './Planets';
import { cardCountOf, onWindowResize, placeCameraAtDistance, raycast, raycastFromCentre, raycastImages, setObjectPositionToFOV, updateImagePosition } from '../../Utils/GlobalFunctions';
import { createGestureLatch } from '../../Utils/clickGesture';
import { getSnapshot } from '../../Utils/loadProgress';
import { RADIANS_PER_PIXEL, wheelRotation } from '../../Utils/carouselRotation';
import { ringZoomFor } from '../../Utils/filmStrip';
import { cardFacingQuaternion, cardFlightPose, cardRestDistance } from '../../Utils/cardLanding';
import { lightboxArgsFor, watchForClose } from '../../Utils/artLightbox';
import { restRing, ringOpacity, setFocusedPlanet } from '../../Utils/ringGroup';
import gsap from 'gsap';
import { loadLobbyContainer, openDoors } from './LobbyContainer';

export let inFocus = false;
export let doorsOpen = false;
let animating = false;

export function setIsAnimating(isAnim) {
    animating = isAnim;
    return animating;
}

export function getIsAnimating() {
    return animating;
}


export function initMainScene() {

    loadMainScene();

    // OrbitControls is not ours to gate in the handlers below — it listens on the canvas itself
    // — and the loading overlay lets a drag and a wheel notch through to it exactly as it let
    // the door click through: a drag orbited the camera and a wheel zoomed it, both behind the
    // loader. LobbyWalkControls switches the controls off for the walk as well, but that runs in
    // the gate's own load callback and the gate is the largest asset in the scene, so it is
    // late. Placed after loadMainScene, which builds the controls and turns them on; closeDoors
    // hands them back, once the gallery is the thing on screen.
    cameraControls.enabled = false;

    loadLobbyContainer();

    loadCelestialBodies();

    const clock = new THREE.Clock();

    // The anchor a drag measures from, in client pixels. A mouse and a finger both write it now,
    // so it is named for the pointer rather than for the device that happened to report it.
    const previousPointerPosition = new THREE.Vector2();

    const camera = getActiveCamera();
    //const postprocessing = { enabled: true, composer: new EffectComposer(renderer) };
    //const shaderSettings = { rings: 4, samples: 3 };

    //let distance = 100;
    const gesture = createGestureLatch();
    let prevPlanetSelection = null;
    let planetSelection = null;
    let imgSelection = null;
    let img = null;
    let prevhoverSelection;
    let imgFocused = false;

    // How long one wheel notch takes to be delivered, in seconds. A notch is roughly one card's
    // share of the ring — see Utils/carouselRotation — so this is the number to reach for if that
    // turn reads too slow or too sharp. A single notch glides for this long; a fast spin
    // accumulates its notches into one continuous glide rather than queueing a turn per notch.
    const GLIDE_SECONDS = 0.3;

    // Two running totals, in radians: what the wheel has asked for, and what the ring has been
    // turned by. While a glide is running they differ by exactly the frame's own step, and that
    // difference is the rotation still to deliver.
    let wheelTarget = 0;
    let delivered = 0;
    const glide = { angle: 0 };

    // The pointer's last known position on the canvas, kept as the two fields the ray actually
    // reads rather than as the event that carried them: a glide ends between events, so the wheel
    // event that started it is long gone by then, and a mousemove that arrived mid-glide was
    // answering a question about a ring that had not stopped moving yet.
    const lastPointer = { offsetX: 0, offsetY: 0 };

    // ─── The ring zoom ────────────────────────────────────────────────────────────
    //
    // One radius for every ring, so a ring holding more works than that circle seats at the
    // preferred frame size gives its frames less width instead — and those frames are smaller on
    // screen than a roomy ring's. Closing on the ring is what makes that up: the camera slides
    // along the axis it already holds with the planet, from the landed position in towards the
    // frames.
    //
    // One size is aimed at and every ring is brought to it — the gallery's most crowded ring at
    // full zoom, which is the Sun's own zoomed view. The rule is Utils/filmStrip's `ringZoomFor`,
    // and it is a ratio of frame widths rather than a shortfall, which is the whole of what "add
    // the zoom to all planets" asked for: a shortfall is zero on a ring that is not crowded, so
    // the control could never appear on one.
    //
    // Held in planet radii, and never negative: the distance the camera is placed at is the
    // landing minus this, and both are world units only once the radius is applied.
    let ringZoom = 0;
    let ringZoomMax = 0;
    let ringZoomLanding = 0;
    let ringZoomTween = null;

    // The whole travel is worth this much of up-drag, so the gesture is a fraction of it rather
    // than a fixed count of radii — a ring that closes a little and one that closes a long way are
    // both a drag of the same length. About one comfortable one-handed sweep.
    const ZOOM_DRAG_PIXELS = 250;

    const ZOOM_BUTTON_ID = 'ringZoom';
    const ZOOM_SHOWN_CLASS = 'ringZoom--shown';

    const BACK_BUTTON_ID = 'backHome';
    const BACK_SHOWN_CLASS = 'backHome--shown';

    /** Puts the camera where `ringZoom` radii of dolly say it stands. */
    function applyRingZoom() {
        placeCameraAtDistance(planetSelection, camera,
            ringZoomLanding - ringZoom * planetSelection.userData.radius);
    }

    /** Dials the zoom to `radii`, clamped to what this ring allows, and moves the camera to match. */
    function setRingZoom(radii) {
        ringZoom = Math.min(ringZoomMax, Math.max(0, radii));

        applyRingZoom();
        showRingZoom();
    }

    /**
     * The control's two states, which are one question asked twice: is there ring left to close on.
     *
     * `+` while there is, `−` once the artwork is at the size the rest of the gallery shows, and
     * the same answer in the label a screen reader reads. `visible` is the caller's — whether a
     * planet is in focus — and a ring with nothing on it stays hidden whether or not one is.
     */
    function showRingZoom(visible = true) {
        const button = document.getElementById(ZOOM_BUTTON_ID);

        if (!button) return;

        const full = ringZoomMax > 0 && ringZoom >= ringZoomMax;

        button.classList.toggle(ZOOM_SHOWN_CLASS, visible && ringZoomMax > 0);
        button.querySelector('.ringZoom__sign').textContent = full ? '−' : '+';
        button.setAttribute('aria-label', full ? 'Zoom out from the artwork' : 'Zoom in on the artwork');
    }

    /** The far end from where the zoom is now: full size from anywhere short of it, then back. */
    function toggleRingZoom() {
        if (!ringZoomMax || animating) return;

        if (ringZoomTween) ringZoomTween.kill();

        const to = ringZoom < ringZoomMax ? ringZoomMax : 0;
        const dial = { radii: ringZoom };

        ringZoomTween = gsap.to(dial, {
            radii: to,
            duration: 0.8,
            ease: 'power2.inOut',
            onUpdate: () => setRingZoom(dial.radii),

            // The destination is written a second time, on arrival, and that is not tidiness: gsap
            // lands its end value rounded to five decimals. Measured, a glide aimed at
            // 0.147810303754689 settles on 0.14781 — so a tween out to the ring stopped a hair
            // short of the max it was aimed at, and since both the sign above and the destination
            // on the next click are read from `ringZoom` against `ringZoomMax`, that hair was the
            // whole of it: the sign never turned to `-`, and clicking again re-aimed at full size
            // instead of ever coming back. Nothing in the arithmetic was wrong. The value the
            // arithmetic was handed was.
            onComplete: () => {
                ringZoomTween = null;
                setRingZoom(to);
            },
        });
    }

    /** One drag frame's worth of zoom. `pixels` is up-positive: dragging up closes on the ring. */
    function zoomRing(pixels) {
        if (!ringZoomMax) return;

        // The button's own glide is abandoned the moment the drag takes the gesture back, or the
        // two write `camera.position` in the same frame and the move stutters against itself.
        if (ringZoomTween) {
            ringZoomTween.kill();
            ringZoomTween = null;
        }

        setRingZoom(ringZoom + (pixels * ringZoomMax) / ZOOM_DRAG_PIXELS);
    }

    // ─── The way home ─────────────────────────────────────────────────────────────
    //
    // The two levels Escape already takes, as one button. Nothing here does anything new: a card
    // that is out is `deselectImage`'s, a planet in focus is `deselectPlanet`'s, and the keydown
    // handler below has given them in this order since it was written. What the button adds is a
    // screen to press — and it is the screen that needs it: a phone has no Escape key, and the
    // card, the ring and the planet were all ways in with no way back out on the one device where
    // the magnifier and the drag now carry every other gesture between them.

    /** Whether the way home is offered. Shown for the length of a visit, and no longer. */
    function showBack(visible = true) {
        const button = document.getElementById(BACK_BUTTON_ID);

        if (!button) return;

        button.classList.toggle(BACK_SHOWN_CLASS, visible);
    }

    /**
     * Out of the deepest thing that is open, one press per level — the same order the keyboard
     * gets, so a visitor who has both can press either.
     *
     * The guard is Escape's, and it is what orders the levels rather than merely permitting them:
     * while the card is still flying home or the planet is still being arrived at, `animating`
     * holds, and a second press cannot skip past a level it never saw.
     */
    function goBack() {
        if (animating || !doorsOpen) return;

        // A card first: it is the smaller thing, and the one the visitor is looking at.
        if (imgFocused) {
            deselectImage();
            return;
        }

        if (inFocus) deselectPlanet(prevPlanetSelection);
    }

    // ─── The category row ─────────────────────────────────────────────────────────
    //
    // `NavBar` draws the gallery's six categories in 3D and calls back here on a press. Two of
    // those cases are nothing new and one is: from home the call is a planet click with the ray
    // replaced by a word, and a press on the planet the camera is already on is dropped. What is
    // new is the third — one ring to another.
    //
    // It is new because the canvas cannot express it. Once anything is focused the click handler
    // sends every press to the artwork, so no press can reach another planet's mesh, and there is
    // consequently no path from one ring to a second but the trip home in between. The row is
    // outside that: it is on its own layer, behind its own raycaster, and it can name any planet
    // at any moment.
    //
    // What it must not do is reach that by way of `deselectPlanet`, which is a flight home and
    // nothing else — two seconds travelling away from the wall to come back to a different part
    // of it. So the departure half is taken and the flight is not. See `leaveRing`.

    /**
     * A category button, or the coin's own press routed here. Returns nothing, and does nothing
     * at all while a flight is in the air: the row is a control over a moving camera, and a
     * second selection mid-glide would be two tweens on the same position.
     */
    function switchPlanet(next) {
        if (animating || !doorsOpen) return;
        if (!next || !next.userData.isPlanet) return;

        if (!inFocus) {
            // From home. `selectPlanet` reads `planetSelection` rather than its own argument —
            // its first line of work is `planetSelection.layers.set(1)` — so the closure is
            // pointed at the new planet before the call here, exactly as the click handler does
            // it. That the click handler's own assignment is a few lines above its call, and this
            // one is beside it, is the whole of this branch.
            planetSelection = next;
            selectPlanet(next);
            return;
        }

        const leaving = prevPlanetSelection;

        if (!leaving || leaving === next) return;

        // A card that is out goes home first, and the move across waits for it to land.
        // Not for tidiness: `deselectImage` clears `animating` in its own `onComplete`, about a
        // second in, while the flight across runs for two — started together, the scene would be
        // announcing itself idle with the camera still in the air, and the whole of the guard
        // above would be gone for that second.
        if (imgSelection) {
            deselectImage(() => leaveRing(leaving, next));
            return;
        }

        leaveRing(leaving, next);
    }

    /**
     * The departure half of `deselectPlanet`, with the camera left where it is.
     *
     * A ring that is being left has three things to put away before another can be arrived at,
     * and they are the same three `deselectPlanet` does — its cards back to layer 1 and faded,
     * its label back to its rest pose, the zoom that belonged to it dropped — so this reads the
     * same calls rather than a paraphrase of them. What it leaves out is the only part of that
     * function that is about going home rather than about leaving.
     *
     * Three further things are deliberately not repeated from there, because the arrival that
     * follows does them and there is no gap between the two: `setFocusedPlanet` is called by
     * `selectPlanet` itself; the leaving ring's *furniture* — mesh, label, ring — is faded by
     * that same arrival's traverse, which answers 0 for every planet that is not the one being
     * arrived at; and neither the magnifier nor the Back is put away, because a visitor moving
     * between two rings has not left the level the two controls belong to.
     */
    function leaveRing(leaving, next) {
        const duration = 2;

        // The kill `deselectPlanet` makes, for the reason it makes it: a tween still running
        // against `ringZoom` would go on writing the variable `selectPlanet` is about to reset,
        // and it would be writing a number about the ring being left.
        if (ringZoomTween) {
            ringZoomTween.kill();
            ringZoomTween = null;
        }

        const label = leaving.getObjectByName(leaving.userData.labelName);
        const rest = leaving.userData.labelPosition;

        planets.traverse(obj => {
            if (!obj.isMesh) return;

            if (obj.userData.isImage && obj.userData.group === leaving.name) obj.layers.set(1);

            // `focused: false` against the planet being left, which is the same pair of arguments
            // departure passes — and the planet being arrived at is why this call cannot be
            // skipped as merely the departure's own business.
            //
            // A selection fades every planet but its own to 0, furniture included, and only the
            // departure brings them back. Arriving at B without a departure from A would
            // therefore leave B's mesh, label and ring at 0 and its own arrival's traverse unable
            // to help: that walk answers `null` — leave it alone — for exactly the items that
            // belong to the planet being arrived at. So the line below is also what puts the new
            // planet back on screen, by giving every planet that is not the one being left the
            // answer departure gives them.
            const opacity = ringOpacity(obj, { planetName: leaving.name, focused: false });

            if (opacity !== null) gsap.to(obj.material, { opacity, duration });
        });

        // The label of the ring being left, back to where it rests. It is the one piece the
        // arrival's own traverse will not touch: that walk repositions its *own* planet's label,
        // and passes over the others.
        if (label) {
            gsap.to(label.position, { x: rest.x, y: rest.y, z: rest.z, duration });
            gsap.to(label.scale, { x: 1, y: 1, z: 1, duration });
        }

        // Pointed at the new planet before the call, for the reason given in `switchPlanet`.
        planetSelection = next;
        selectPlanet(next, duration);
    }

    renderer.autoClear = false;

    setEventListeners();    //setGuis();

    // The fourth argument is the HUD's licence to act. `doorsOpen` is this file's own answer to
    // whether the lobby is finished with, and it is the exact question the coin's hover asks —
    // in the lobby it is a logo, and once the doors are open it is the toggle for the row.
    //
    // The fifth is what shutting the row means. It is `goBack`, the same function the Back button
    // and the second level of Escape both run, handed over whole rather than paraphrased: the row's
    // press and the keyboard's press are one intention, and the order they resolve in — a card, then
    // the planet — is already written once, in there.
    setAnimation(renderer, scene, switchPlanet, () => doorsOpen, goBack);
    animate();

    function setEventListeners() {

        const canvas = document.getElementById('canvasEl');

        // The magnifier. Shown and hidden by class rather than with the `hidden` attribute, the
        // way the Skip is — any author `display` beats `hidden` — and a control taken off screen
        // by `display` leaves the tab order with it, which is what a control with nothing to do on
        // this planet should do. It is a real button, so the keyboard reaches it too.
        //
        // It is a child of the element every listener below hangs off, so each of these events
        // would otherwise arrive twice: once here and once as an event on the scene. A press that
        // began on the magnifier would arm the drag latch and go on turning the ring as the
        // pointer left the button, and the button's own click would be read as a ray into the
        // scene — offset against the button rather than the canvas, so it would come out somewhere
        // nobody had pointed at. Swallowed at the button, which is the only place that knows the
        // pointer is on a control and not on the ring.
        const zoomButton = document.getElementById(ZOOM_BUTTON_ID);

        if (zoomButton) {
            zoomButton.addEventListener('click', toggleRingZoom);

            // `touchstart` and `touchmove` are in the list with the mouse events because the
            // isolating has to hold for whichever pointer is on the button: a finger that begins a
            // press on the magnifier must not arm the ring's drag latch and then turn the ring as
            // it slides off the button, exactly as a mouse must not.
            ['mousedown', 'mousemove', 'mouseup', 'click',
             'touchstart', 'touchmove', 'touchend'].forEach(type =>
                zoomButton.addEventListener(type, event => event.stopPropagation()));
        }

        // The way home, on the same terms and for the same reason as the magnifier above: it is a
        // child of the element every listener here hangs off, so its press would otherwise arrive
        // twice — once as the button's and once as a ray into the scene, aimed from a point offset
        // against the button rather than the canvas. Swallowed at the button.
        const backButton = document.getElementById(BACK_BUTTON_ID);

        if (backButton) {
            backButton.addEventListener('click', goBack);

            ['mousedown', 'mousemove', 'mouseup', 'click',
             'touchstart', 'touchmove', 'touchend'].forEach(type =>
                backButton.addEventListener(type, event => event.stopPropagation()));
        }

        canvas.addEventListener('dblclick', (event) => {
            event.preventDefault();

            if (animating || !doorsOpen || !inFocus || imgFocused) return;

            deselectPlanet(prevPlanetSelection);
        })

        canvas.addEventListener('mousemove', (event) => {
            event.preventDefault();

            // Recorded before the guard, because where the pointer is is true whether or not this
            // frame is allowed to act on it.
            lastPointer.offsetX = event.offsetX;
            lastPointer.offsetY = event.offsetY;

            // don't raycast when moving
            if (animating) return;

            if (gesture.isPressed()) {
                gesture.move();
                if (inFocus && !imgFocused) dragRing(event.clientX, event.clientY);
            } else {
                // Hover is off while an artwork is selected. The card out in front of the camera
                // is the subject then, and the hover scale is not merely decoration on it: the
                // selected card used to keep the 1.2x a hover had left it at, so an artwork
                // clicked while the pointer happened to be over it flew at a size a fifth larger
                // than the one it settles at, and settled at that size too.
                hoverUnderPointer(event);
            }
        })

        canvas.addEventListener('click', (e) => {

            e.preventDefault();

            // Read and cleared here, before any branch can act on it, because every click
            // ends a gesture. This test used to sit further down, on the branch that only
            // runs once the doors are open, and the drag flag it cleared was set on any
            // movement with the button held. The lobby's door ray returns early by design,
            // so the drag that aimed the reticle at the button — the documented way to look
            // when the pointer lock is refused — survived into the gallery, and the first
            // click on a planet was swallowed as though it had been a drag. One dead click,
            // then normal. The latch was never broken; it was simply not consulted on the
            // one path that happened to run.
            const wasLook = gesture.consumeClick();

            // Nothing on this canvas is clickable while the loading overlay is still up. The
            // overlay is pointer-transparent by design, so a click on it lands here and fires
            // the ray below — which, before the scene is ready, opened the doors and started
            // the whole opening move out of sight behind the loader. Asked after the latch on
            // purpose: returning above that line would leave the drag latch set and swallow
            // the first real click once the scene is up.
            if (!getSnapshot().finished) return;

            if (!doorsOpen) {
                // The lobby has no cursor to aim with: the reticle at the centre of the
                // frame is the aim (LobbyWalkControls), so the click is a ray down the
                // middle of the frame rather than at the pointer. Clicking anywhere is
                // clicking the reticle.
                raycastFromCentre(scene.children, hitTarget => {
                    if (hitTarget) {
                        const t = hitTarget.object;
                        if (t.name === 'DoorButton') {
                            doorsOpen = true;

                            openDoors();
                        }
                    }
                })
                return;
            }

            //do a check to make sure raycasting is not available if with scrolling mouseup
            if (wasLook) return;

            //disable clicking while animating
            if (animating) return;

            //if focused on one planet and its images, set raycast for the images, else set it to planets
            if (inFocus) {
                const target = planetSelection.getObjectByName(planetSelection.userData.imgGroupLabel);

                if (!target || target.children.length === 0) return;

                raycastImages(e, planetSelection, target, hitTarget => {
                    if (hitTarget) {

                        img = hitTarget.object;
                        if (imgSelection) {
                            if (imgSelection === img) {
                                deselectImage();
                            } else {
                                deselectImage(selectImage);
                            }
                        } else {
                            selectImage(img);
                        }
                    }
                });
            } else {
                raycast(e, planets.children, (hitTarget => {
                    if (hitTarget) {
                        const target = hitTarget.object;
                        planetSelection = target.parent;
                        if (planetSelection.userData.isPlanet)
                            selectPlanet(planetSelection);
                    }
                }))
            }
        });

        canvas.addEventListener('mousedown', (event) => {
            event.preventDefault();
            previousPointerPosition.copy(new THREE.Vector2(event.clientX, event.clientY));
            gesture.press();

            // With the button down the pointer is a handle rather than a pointer, and the drag
            // it is about to start does not re-ask what is under it — the ring turns and the ray
            // stays where it was. So the hover it arrived with is put back here, or the card it
            // was on stays lit, and travels along the ring, for as long as the drag lasts.
            clearHover();
        });

        canvas.addEventListener('mouseup', (event) => {
            event.preventDefault();

            gesture.release();
        });

        // The same drag, with a finger on it. Without these the film strip was the one control on
        // the page a touchscreen could not work at all: `scrollImages` is only ever reached from a
        // press, and `press()` was only ever armed by `mousedown` — which a touchscreen sends after
        // the tap is over, if it sends one at all. So the canvas took no drag and the ring never
        // turned, while the artwork on it looked like something that should slide.
        //
        // The point comes off `touches[0]`, because a touch event carries no `clientX` of its own;
        // the field on the touch is the same one a mouse reports, which is why both devices come
        // through `dragRing` below rather than each carrying its own copy of the turn.
        canvas.addEventListener('touchstart', (event) => {
            const touch = event.touches[0];

            if (!touch) return;

            previousPointerPosition.copy(new THREE.Vector2(touch.clientX, touch.clientY));
            gesture.press();
            clearHover();
        }, { passive: true });

        canvas.addEventListener('touchmove', (event) => {
            const touch = event.touches[0];

            // A second finger is not a drag of the ring. `touch-action: none` on #canvasEl has
            // already taken the pinch off the browser, so this is a gesture that does nothing
            // rather than a gesture that zooms the page.
            if (!touch || event.touches.length > 1 || animating || !gesture.isPressed()) return;

            // Marked whether or not this frame is ours, exactly as the mouse handler marks it: the
            // drag the finger made is the drag the click after it has to be able to see.
            gesture.move();

            // With no planet in focus the drag belongs to the camera controls — the same drag a
            // mouse makes out here — so it is left to them.
            if (!inFocus || imgFocused) return;

            // Past that guard the drag *is* the ring's, and the event is taken away from the
            // browser so nothing pans or rubber-bands under it. This is the belt, not the gate:
            // `touch-action: none` on #canvasEl is what stops a gesture being claimed as a scroll
            // before the first move arrives, and a browser that has already begun one cannot always
            // be cancelled out of it.
            if (event.cancelable) event.preventDefault();

            // The turn only. The magnifier is the touchscreen's zoom — see `dragRing`.
            dragRing(touch.clientX, touch.clientY, false);
        }, { passive: false });

        // `touchcancel` is the browser taking the gesture back — a call arriving, or the OS
        // claiming an edge swipe — and it has to release the latch the way a lift does. A latch
        // left pressed is a ring that follows the next mouse move without a button held.
        const endTouch = () => gesture.release();

        canvas.addEventListener('touchend', endTouch);
        canvas.addEventListener('touchcancel', endTouch);

        // The wheel turns the ring, the same rotation the drag above turns it by — a drag
        // reports pixels of pointer travel and a wheel reports notches, and
        // Utils/carouselRotation is what puts the two in one unit.
        //
        // Registered non-passive because the rotation *is* the gesture: without preventDefault
        // the page would scroll (or the browser zoom, on a pinch) at the same time as the ring
        // turned. So the guard comes before it, and returns before the event is taken away from
        // anything else — at home and in the lobby the wheel is the camera controls' zoom, and
        // in the planet view the controls are disabled while the ring is the thing under the
        // pointer.
        canvas.addEventListener('wheel', (event) => {
            lastPointer.offsetX = event.offsetX;
            lastPointer.offsetY = event.offsetY;

            if (!doorsOpen || !inFocus || animating || imgFocused) return;

            const radians = wheelRotation(event);

            if (radians === 0) return;

            event.preventDefault();
            // The same rotation the drag above turns by, and now the same smoothness — but
            // arrived at differently, because the two devices report it differently. See
            // turnByWheel: a drag is already a stream of small per-frame deltas, and a notch is
            // a whole card in one, so only the wheel's is spread over the frames it needs.
            turnByWheel(radians);

            // The pointer is about to be over different cards than it is now, so the ring is put
            // back to rest rather than asked — an answer given here would be stale a frame later,
            // once the glide has carried the ring on. The ask happens where the glide settles,
            // which is turnByWheel's onComplete.
            clearHover();
        }, { passive: false });

        // Escape, the second of its two levels in this scene. The first is the lightbox's own:
        // its Escape is wired inside SamsonArt's js/viewer.js, and Utils/artLightbox watches it
        // close and sends the card home. This is the press after that one — out of the planet,
        // and back to the lobby's view of it.
        //
        // The guard is the dblclick handler's, deliberately: that is the same decision one
        // gesture apart. It is also what orders the two levels. While the lightbox is open, or
        // the card is still flying home, `imgFocused` is true and this does nothing; Escape means
        // the planet only once the card is back on its ring, so two quick presses cannot skip a
        // level.
        //
        // No preventDefault: Escape is the browser's too — it releases the lobby's pointer lock
        // and it leaves fullscreen — and neither of those is ours to take away.
        window.addEventListener('keydown', (event) => {
            if (event.key !== 'Escape') return;

            // A card that flew out with no lightbox behind it — the viewer script missing, or a
            // record with no url — has nothing that would ever send it home, because
            // `openLightbox` returns before it starts watching. Clicking the card again is the
            // other way out of that; this is the one that does not require knowing it.
            if (imgFocused && !animating && !isLightboxOpen()) {
                deselectImage();
                return;
            }

            if (animating || !doorsOpen || !inFocus || imgFocused) return;

            deselectPlanet(prevPlanetSelection);
        });

        // The arrow body was the function reference, so nothing was ever invoked and the
        // canvas and the camera's aspect stayed at whatever they were on load. The four
        // arguments are the ones onWindowResize was written for.
        window.addEventListener('resize', (e) => {
            onWindowResize(renderer, camera, canvas, e);

            // The beam is measured against the frame, so a frame that has changed size has to be
            // measured against again. On a phone this is the common case rather than the rare one:
            // the address bar collapsing and the handset being turned both land here.
            fitSpotlightToCamera();
        });

        cameraControls.addEventListener('change', (e) => {
            if (e.target === null) {
                console.log("change", e);
            }

        })
    }

    /** The gallery's lightbox, if the page's script tag has loaded it. */
    function lightbox() {
        return window.SamsonViewer || null;
    }

    /** Whether that lightbox is on the page and currently showing a work. */
    function isLightboxOpen() {
        const viewer = lightbox();

        return !!(viewer && viewer.isOpen());
    }

    // Hands one artwork to the gallery's own lightbox, once its card has finished turning to
    // face the camera. The viewer is a script tag on the page, not an import — see
    // Utils/artLightbox.js — and if it is not there the click simply does what it always did.
    function openLightbox(img) {
        const viewer = lightbox();
        const args = lightboxArgsFor(img.userData.art);

        if (!viewer || !args) return;

        // The overlay covers the canvas, so anything that would have reached the controls
        // behind it is unreachable anyway. Switching them off keeps the planet still while the
        // work is being looked at; the state after closing is deselectImage's to set, as before.
        cameraControls.enabled = false;
        viewer.open(args.src, args.title, args.mediaType);

        // Closing it — by the ✕, the backdrop or Escape — spins the card back to its place.
        watchForClose(viewer, () => deselectImage());
    }

    // The flight an artwork takes when it is picked out of the ring, and the pose it lands in.
    //
    // Everything here is measured in the planet's own radii, which is what makes it the same on
    // every planet — Utils/cardLanding holds that arithmetic. The destination used to be read
    // off `userData.maxZimgPos`, a running maximum held in a module global and shared by every
    // ring: once the Sun's ring (radius 14.5, the largest body in the scene) had been through
    // resetImagePositions, every planet built after it inherited 15.95 world units as its own
    // ring's maximum. The card therefore came to rest 1.5 radii in front of the camera on the
    // Sun and tens of radii *behind* the viewer on Earth and Mercury, and which planets were
    // wrong depended on which ring happened to finish loading first. The spin had its own
    // version of the same fault: it turned about the parent's Z, which on a ring that is not
    // facing the camera is not the card's own axis — it tumbled the off-axis cards and left
    // half the ring back-facing, in a proportion set by how many cards that planet had.
    function selectImage(img) {
        // The ring stops where it is, rather than gliding on under the card that just left it.
        stopRing();
        animating = imgFocused = true;
        imgSelection = img;

        // The hover latch starts again from nothing. The card being clicked is usually the
        // hovered one — that is how the pointer got there — and a latch still naming it would
        // swallow the first hover it is given after it comes home, leaving it the only card on
        // the ring that never scales up.
        prevhoverSelection = null;

        // Snapshotted before anything moves, so the flight back has a pose to return to. The
        // quaternion rather than the Euler angles: the animation drives the quaternion, and a
        // triple of Euler angles only means anything against the order it was written in.
        imgSelection.userData.initialPos = new THREE.Vector3().copy(img.position);
        imgSelection.userData.initialQuat = new THREE.Quaternion().copy(img.quaternion);

        // At its own size, whatever a hover may have left it at — see the hover branch above.
        const scale = img.userData.scale;
        img.scale.set(scale, scale, scale);

        // In the ring's own frame, which is the world's: the planet group carries no rotation,
        // and its position is the origin of this frame. The camera has landed on this planet's
        // +Z axis at `landingDistance`, less whatever ring zoom the visitor has dialled in — so a
        // point on that same axis one card-gap short of the camera is the middle of the frame, at
        // a size that does not depend on the radius. This is the position `img.position` is
        // written in. The zoom goes with the radius because the card rests a card-gap in front of
        // wherever the camera is, and both ends of that move have to agree or the card lands too
        // big behind the viewer.
        const endPosition = new THREE.Vector3(0, 0,
            cardRestDistance(planetSelection.userData.radius, camera.fov, camera.near, camera.aspect,
                ringZoom));

        // The same point in the world, for the two questions that are asked in world space:
        // where the camera is from here, and what the camera controls should orbit around.
        const endWorldPosition = endPosition.clone().add(planetSelection.position);

        const startPosition = img.position.clone();
        const startQuat = img.quaternion.clone();
        const endQuat = cardFacingQuaternion(endWorldPosition, camera.position);

        const flight = { t: 0 };

        gsap.to(flight, {
            t: 1,
            duration: 1,
            onUpdate: () => {
                // The turn to face the camera and the spin about the card's own face, composed
                // in one place so they cannot drift apart. Utils/cardLanding holds it, and the
                // tests there sample it — a whole turn, landing on the aimed pose with nothing
                // left over, from every angle of the ring and on every planet.
                cardFlightPose(img.position, img.quaternion,
                    startPosition, endPosition, startQuat, endQuat, flight.t);
            },
            onComplete: () => {

                animating = false;
                cameraControls.enabled = true;
                // The card's own position in the world. This was the destination in the ring's
                // frame, handed to the controls as though the planet stood at the origin — and
                // it does not: Planets.js places each one out along its own angle.
                cameraControls.target = endWorldPosition;
                cameraControls.enablePan = true;
                cameraControls.enableRotate = true;
                cameraControls.enableZoom = true;
                cameraControls.maxZoom = 4;

                // The turn is done, so the work is facing the camera before the overlay arrives.
                openLightbox(img);
            }
        });
    }

    // The same flight, run backwards, and the same rule: one whole turn about the card's own
    // face, the other way round, so it arrives exactly on the pose it was found in — position
    // and orientation both, rather than a few degrees short of it.
    function deselectImage(callback) {
        // Already on its way home, or nothing is out to send: both are reachable. The lightbox
        // watch polls for a closed viewer, so a close that something else has already acted on
        // arrives here a fraction of a second later as a second call, and a second flight on the
        // same card is two tweens writing one position.
        if (animating || !imgSelection) return;

        animating = true;

        const image = imgSelection;

        const startPosition = image.position.clone();
        const startQuat = image.quaternion.clone();
        const endPosition = image.userData.initialPos;
        const endQuat = image.userData.initialQuat;

        const flight = { t: 0 };

        gsap.to(flight, {
            t: 1,
            duration: 1,
            onUpdate: () => {
                // The same flight the other way round: `-1` turn rather than a second recipe.
                cardFlightPose(image.position, image.quaternion, startPosition, endPosition,
                    startQuat, endQuat, flight.t, -1);
            },
            onComplete: () => {
                animating = imgFocused = false;
                cameraControls.enablePan = true;
                cameraControls.target = image.parent.parent.position;
                cameraControls.enableRotate = true;
                cameraControls.enableZoom = true;
                cameraControls.enabled = false;

                imgSelection = null;
                if (callback)
                    callback(img);
            }
        });
    }

    /**
     * Puts the ring back to rest and forgets which card was lit.
     *
     * The three callers are the three reasons a hover stops being true: the pointer carried on
     * past the ring, the button went down and the pointer became a handle, and the wheel asked
     * for a turn so the card under it is about to move. `restRing` alone is not enough in any of
     * them — `hoverImage` only fires for a card the latch is not already naming, so a latch left
     * holding a card would leave that one card unable to light again.
     */
    function clearHover() {
        if (!prevhoverSelection) return;

        restRing(prevhoverSelection.parent.children);
        prevhoverSelection = null;
    }

    /** Lights `image`, and puts the rest of its ring back to their own size. */
    function hoverImage(image) {
        restRing(image.parent.children);

        image.scale.setScalar(image.userData.scale * 1.2);

        prevhoverSelection = image;
    }

    /**
     * Points the hover at whatever card is under the pointer, and takes it off everything else.
     *
     * Called on a plain mousemove, and again after the ring has turned — the same pointer is
     * over a different card once the ring has moved — so the two cases cannot disagree about
     * which card is lit. It carries its own guard rather than relying on its callers, because
     * both of the callers have a reason to be here and only one of them is a mousemove.
     *
     * The ray used to act only on a hit, which left the last hovered card enlarged for good:
     * hovering lights a card and puts the rest of the ring back, and nothing undid that when the
     * pointer carried on past the ring into empty space. A hit is what lights a card; a miss is
     * what puts them all back.
     */
    function hoverUnderPointer(event) {
        if (!inFocus || imgFocused || !planetSelection) return;

        const targets = planetSelection.getObjectByName(planetSelection.userData.imgGroupLabel);

        if (!targets || targets.children.length === 0) return;

        raycastImages(event, planetSelection, targets, hitTarget => {
            if (hitTarget) {
                if (hitTarget.object !== prevhoverSelection) hoverImage(hitTarget.object);
            } else {
                // Under the pointer there is now nothing: the whole ring back to its own size.
                clearHover();
            }
        });
    }

    /**
     * Turns the ring by `radians`, and re-places every card where it now belongs.
     *
     * The one place a rotation is applied, whatever asked for it: a drag and a wheel are the
     * same turn from two devices, and a second copy of this loop is a second place for them to
     * drift into two speeds. Each card keeps its own angle and is placed from it, so the ring
     * stays a rigid circle — `updateImagePosition` squares each card to the planet again as it
     * moves, which is what keeps them tangent to the ring instead of carried round at a slant.
     */
    function rotateRing(radians) {
        if (!planetSelection || radians === 0) return;

        const group = planetSelection.getObjectByName(planetSelection.userData.imgGroupLabel);

        if (!group) return;

        group.children.forEach(img => {
            if (!img.userData.isImage) return;

            img.userData.angle += radians;
            updateImagePosition(img, planetSelection);
        });

        // The strip turns with the ring, by the same radians about the same axis and origin, so
        // its windows stay on the cards without either being re-aligned from the other. It is not
        // in the loop above because a card is re-placed from its own angle while the band is one
        // rigid turn — and because Utils/filmStrip's `frameCentreU` is what makes the two agree,
        // not a second copy of the angle arithmetic in here.
        const band = group.userData.filmStrip;

        if (band) band.rotateY(radians);
    }

    /**
     * A wheel notch, spread over the next `GLIDE_SECONDS` rather than applied at once.
     *
     * The drag needs nothing of this: a mouse drag reports its travel as a stream of ~5px
     * events, one per frame, and is smooth for that reason alone. A notch arrives as one event of
     * ~100px — a whole card's share of the ring at RADIANS_PER_PIXEL — and applied as it comes it
     * is a card's jump inside a single frame. That is the whole of the difference between the two
     * gestures' feel. So the ease is only ever the wheel's: putting one on the drag as well would
     * leave the ring trailing the cursor.
     *
     * `overwrite` is what keeps a fast spin from queueing a tween per notch: each notch replaces
     * the running one with a tween heading for the new total, from wherever the ring has got to.
     * A single notch therefore glides and stops on the angle it asked for, and a spin keeps the
     * target ahead of the ring and glides continuously until the last of it arrives — on that
     * angle, with no momentum of its own to carry it past.
     */
    function turnByWheel(radians) {
        wheelTarget += radians;

        gsap.to(glide, {
            angle: wheelTarget,
            duration: GLIDE_SECONDS,
            ease: 'power2.out',
            overwrite: true,
            onUpdate: () => {
                rotateRing(glide.angle - delivered);
                delivered = glide.angle;
            },
            onComplete: () => {
                // The ring has stopped, so the pointer is a pointer again and the question it asks
                // has a settled answer. Asked here rather than left to the next mousemove, or the
                // card the ring came to rest under would stay unlit until the pointer was moved —
                // and the hover cleared when the wheel was last touched is put back by this too.
                //
                // `lastPointer` stands in for an event here: the ray reads only offsetX and
                // offsetY off it, and no event is in hand by the time a glide ends. A notch that
                // overwrites a running tween kills it without running this — gsap answers an
                // overwrite with onInterrupt, not onComplete, measured against the gsap in this
                // repo's node_modules — so a fast spin asks once, on the notch that settles.
                // Were it the other way round, a spin would re-ask on every notch: harmless, but
                // it would raycast the whole ring sixty times a second for an answer it discards.
                hoverUnderPointer(lastPointer);
            },
        });
    }

    /**
     * Drops a glide that is still running, and the rotation it had not yet delivered.
     *
     * A glide outlives the event that started it and the guards that admitted it: the wheel
     * listener tests `animating` and `imgFocused` as each event arrives, but a glide already in
     * flight goes on writing card positions for up to `GLIDE_SECONDS` afterwards. Mid-ring that
     * is invisible; taken over it is not. A selection started inside that window would have the
     * glide and the flight writing the same card's position — the fault item 7 was — and
     * `rotateRing` turns whatever `planetSelection` is on the frame it runs, so a glide left
     * running through a planet change would turn the next planet's ring instead. So the three
     * places that take the ring over stop it first, and the undelivered remainder is dropped
     * rather than arriving later.
     */
    function stopRing() {
        gsap.killTweensOf(glide);
        glide.angle = delivered;
        wheelTarget = delivered;
    }

    /**
     * One turn of the ring, from a pointer position in client pixels.
     *
     * The whole of the drag — the card going back to rest and the turn itself — because a mouse
     * and a finger have to do the same thing here. A branch of its own each would be two places
     * for the ring to be turned from, and the one a touchscreen could not reach is exactly how
     * the film strip came to be unusable on a phone.
     *
     * `allowZoom` is the one thing the two do not share. A mouse drags up the ring deliberately,
     * and has a wheel under the arm and the magnifier beside it if it would rather not; a thumb
     * crossing the strip does not, and every sideways swipe that drifted a few pixels uphill was
     * closing on the ring as well as turning it. On a touchscreen the ring is turned and the zoom
     * is left to the button, which is the one press that says what it is for. Both devices still
     * measure from the same anchor, so a drag that may not zoom still turns the ring from it.
     */
    function dragRing(clientX, clientY, allowZoom = true) {
        if (imgSelection) {
            //Return image to original position and rotation without any animation
            imgSelection.position.copy(imgSelection.userData.initialPos);
            imgSelection.quaternion.copy(imgSelection.userData.initialQuat);
            imgSelection.scale.setScalar(imgSelection.userData.scale);
            imgSelection = null;
        }

        scrollImages(clientX, clientY, allowZoom);
    }

    function scrollImages(clientX, clientY, allowZoom = true) {

        if (planetSelection === null || planetSelection === undefined) return;

        const deltaMove = { x: clientX - previousPointerPosition.x, y: clientY - previousPointerPosition.y };

        // Re-anchored whether or not the ring could be turned, so a frame that turned nothing
        // cannot leave the next one measuring from a stale point and jumping.
        previousPointerPosition.copy(new THREE.Vector2(clientX, clientY));

        // One anchor, two axes: across turns the ring, up closes on it. The vertical was the free
        // one — the wheel turns the ring and so does a sideways drag, so this is the only gesture
        // left that a zoom could take and the only one that had nothing on it. Reading both off
        // the same anchor is what keeps them from stealing each other's pixels: a drag that
        // re-anchored between the two would hand the second a delta of zero.
        //
        // The vertical is a mouse's alone; see `dragRing` for why a finger is given only the turn.
        rotateRing(deltaMove.x * RADIANS_PER_PIXEL);
        if (allowZoom) zoomRing(-deltaMove.y);
    }

    function selectPlanet(planet, duration = 2) {
        console.log('planet selection is', planet);
        // `planetSelection` is already this planet — the click handler sets it before calling
        // here — so a glide still running would be turning the ring being arrived at rather than
        // the one it was started on.
        stopRing();
        inFocus = true;
        animating = true;

        planetSelection.layers.set(1);

        // Where the traverses below are about to put the camera's attention, for anything that
        // joins a ring after they have run — see Utils/ringGroup's `ringBirth`. Art is still
        // arriving at this point on any ring whose textures are slow: a card that lands now, and
        // the band that is built once the ring's last texture does, would otherwise sit at the
        // opacity they are created with, which is 0, for as long as the visitor stays.
        setFocusedPlanet(planet.name);

        cameraControls.enabled = false;

        const radius = planet.userData.radius;

        prevPlanetSelection = planet;

        updateSpotlightTarget(planet);

        const planetLabel = planet.getObjectByName(planet.userData.labelName);
        // Asked for now rather than read off the planet, because the answer depends on the
        // viewport: the framing is against the narrower of the frame's two half-angles, and
        // the aspect is only known live. Planets.js computes the same point once at load for
        // nothing to read — a target frozen at the aspect the page happened to open with.
        const planetEndPosition = setObjectPositionToFOV(planet, camera);

        // What the zoom is allowed to be on this planet, settled once as the visit begins.
        //
        // The most crowded ring in the gallery sets the size every ring is aimed at, so the count
        // is asked of all of them and not only of this one. Utils/filmStrip's `ringZoomFor` is the
        // whole of the arithmetic; this gathers the count it needs and nothing else.
        ringZoom = 0;
        ringZoomLanding = planetEndPosition.distanceTo(planet.position);

        const landingRadii = ringZoomLanding / radius;
        let ringCountMax = 0;

        planets.traverse(object => {
            const held = cardCountOf(object);

            if (held > ringCountMax) ringCountMax = held;
        });

        // The bound on where the zoom may stop used to be kept here, as the gap out to the ring,
        // and it was `RING_RADIUS` — a camera standing *in* the ring, one tenth of a radius off the
        // Sun's surface, which is a frame of nothing but Sun. That is what "it zooms to the centre
        // of the Sun" was, and it needed a bound at all because a crowded ring asked for more
        // travel than the gap it had. `ringZoomFor` asks for the gap in the first place — its stop
        // is `CARD_GAP` outside the ring, the distance a clicked artwork rests at — so there is
        // nothing left here to clamp, and no ring can reach the ring it hangs on.
        ringZoomMax = ringZoomFor(cardCountOf(planet), ringCountMax, landingRadii);

        //Set new position for label to be at the front top of the planet
        const polarAngle = Math.PI / 2.8; // for y axis
        const azimuthalAngle = -24; //for z axis

        const x = planetLabel.position.x;
        const y = radius * Math.cos(polarAngle);
        const z = radius * Math.sin(polarAngle) * Math.sin(azimuthalAngle) + (radius * 0.15);

        const scale = planet.userData.labelScale;

        let startOrientation;
        let targetOrientation;

        planets.traverse(obj => {
            if (!obj.isMesh) return;

            // The layer is the cards' business alone. The strip is on layer 1 so that no ray can
            // reach it — clicking or hovering a frame must be clicking or hovering the artwork
            // inside it — so this write must not be widened to cover the rest of the ring.
            if (obj.userData.isImage && obj.userData.group === planet.name) obj.layers.set(0);

            // What to fade, and what to leave alone, is Utils/ringGroup's call. It is the same
            // call `deselectPlanet` makes, which is what stops the two traverses disagreeing
            // about the strip — they did, and leaving a planet lit the strip on all the others.
            const opacity = ringOpacity(obj, { planetName: planet.name, focused: true });

            if (opacity !== null) gsap.to(obj.material, { opacity, duration });
        });

        startOrientation = planetLabel.quaternion.clone();
        targetOrientation = camera.quaternion.clone().normalize();
        gsap.to(planetLabel.position, {
            x: x,
            y: y,
            z: z,
            duration: duration,
            onUpdate: function () {
                planetLabel.quaternion.copy(startOrientation).slerp(targetOrientation, this.progress());
            },
        });

        gsap.to(planetLabel.scale, {
            x: scale,
            y: scale,
            z: scale,
            duration: duration
        });
        startOrientation = camera.quaternion.clone();
        targetOrientation = planet.quaternion.clone().normalize();
        gsap.to(camera.position, {
            x: planetEndPosition.x,
            y: planetEndPosition.y,
            z: planetEndPosition.z,
            onUpdate: function () {
                camera.quaternion.copy(startOrientation).slerp(targetOrientation, this.progress());
            },
            duration: duration
        }).then(() => {
            animating = false;

            cameraControls.target = planet.position;
            cameraControls.enabled = false;
            cameraControls.update(clock.getDelta());

            planet.layers.set(0);

            // Offered on arrival rather than at the start of the flight, so the control appears
            // with the ring at rest and nothing about it is a thing to reach for mid-glide.
            showRingZoom(true);
            showBack(true);

        });
    }

    function deselectPlanet(planet, callback, duration = 2) {
        // Leaving the ring behind: the wheel's guard closes when `inFocus` goes false at the end
        // of this flight, but a glide already running would go on turning it after that.
        stopRing();

        // The traverse below is the one that fades this ring out, so the selection this module
        // hands a newborn card ends here rather than at `inFocus` — which is not false until the
        // flight lands, two seconds of a card arriving with no selection to answer to.
        setFocusedPlanet(null);

        // The zoom belongs to the visit that is ending, so it goes before the camera does. Left
        // standing, it would be a number about a planet the camera is no longer near — applied
        // again by the next arrival's first drag from a resting place it never came to.
        if (ringZoomTween) {
            ringZoomTween.kill();
            ringZoomTween = null;
        }

        ringZoom = 0;
        ringZoomMax = 0;
        showRingZoom(false);
        showBack(false);

        const planetLabel = planet.getObjectByName(planet.userData.labelName);
        console.log('planet label start', planetLabel.position, planetLabel.rotation)
        console.log('label', planetLabel)


        const lookAtTarget = new THREE.Vector3();
        const cameraEndPosition = new THREE.Vector3().copy(mainCameraInitPosition);
        const labelPos = planet.userData.labelPosition;

        let startOrientation;
        let targetOrientation;

        if (imgSelection) {
            deselectImage();
        }

        planets.traverse(obj => {

            if (obj.userData.isLabel) {

                startOrientation = obj.quaternion.clone();
                targetOrientation = camera.quaternion.clone().normalize();
                if (obj.parent === planet) {
                    gsap.to(obj.position, {
                        x: labelPos.x,
                        y: labelPos.y,
                        z: labelPos.z,
                        duration
                    });
                }

                gsap.to({}, {
                    duration: duration,
                    onUpdate: function () {
                        obj.quaternion.copy(startOrientation).slerp(targetOrientation, this.progress());
                    }
                });
            }
            if (!obj.isMesh) return;

            // The cards go back to layer 1, where they rest and where the raycaster cannot see
            // them: this planet's cards and no others', on the same terms as `selectPlanet`.
            if (obj.userData.isImage && obj.userData.group === planet.name) obj.layers.set(1);

            // The same call `selectPlanet` makes, with `focused` false. That symmetry is the fix
            // for the strip: this traverse used to ask "is this not an artwork?" to mean "is this
            // the planet's own furniture?", which the strip answered yes to, so every departure
            // lit the strip on every planet the camera was not on.
            const opacity = ringOpacity(obj, { planetName: planet.name, focused: false });

            if (opacity !== null) gsap.to(obj.material, { opacity, duration });
        });

        gsap.to(planetLabel.scale, {
            x: 1,
            y: 1,
            z: 1,
            duration: duration
        });

        startOrientation = camera.quaternion.clone();
        targetOrientation = new THREE.Quaternion().setFromEuler(mainCameraInitRotation).clone().normalize();
        gsap.to(camera.position, {
            x: cameraEndPosition.x,
            y: cameraEndPosition.y,
            z: cameraEndPosition.z,
            duration: duration,
            onUpdate: function () {
                camera.quaternion.copy(startOrientation).slerp(targetOrientation, this.progress());
            },
            onComplete: completeAnim
        });

        function completeAnim() {
            cameraControls.enabled = true;
            cameraControls.target = lookAtTarget;
            inFocus = false;

            planet.layers.set(0);

            if (callback instanceof Function) {
                callback();
            }
        }
    }

}



/*
function process() {

    camera.updateMatrixWorld();

    if (effectController.jsDepthCalculation) {

        let targetDistance;

        if (planetSelection) {
            targetDistance = planetSelection.userData.distance;
        }
        else {
            targetDistance = 1000;
        }

        distance += (targetDistance - distance) * 0.03;

        const sdistance = smoothstep(camera.near, camera.far, distance);

        const ldistance = linearize(1 - sdistance);

        postprocessing.bokeh_uniforms['focalDepth'].value = ldistance;

        effectController['focalDepth'] = ldistance;

    }

    if (postprocessing.enabled) {

        renderer.clear();

        // render scene into texture

        renderer.setRenderTarget(postprocessing.rtTextureColor);
        renderer.clear();
        render();

        // render depth into texture

        scene.overrideMaterial = materialDepth;
        renderer.setRenderTarget(postprocessing.rtTextureDepth);
        renderer.clear();
        render();
        scene.overrideMaterial = null;

        // render bokeh composite

        renderer.setRenderTarget(null);

        render(() => renderer.render(postprocessing.scene, postprocessing.camera));

    } else {

        scene.overrideMaterial = null;

        renderer.setRenderTarget(null);
        renderer.clear();
        render();

    }

    function linearize(depth) {

        const zfar = camera.far;
        const znear = camera.near;
        return - zfar * znear / (depth * (zfar - znear) - zfar);

    }

    function smoothstep(near, far, depth) {

        const x = saturate((depth - near) / (far - near));
        return x * x * (3 - 2 * x);

    }

    function saturate(x) {

        return Math.max(0, Math.min(1, x));

    }

}
 
function setGuis() {
    let camGui = new GUI();
    camGui.add(camera.position, 'x', -500, 500, 0.01);
    camGui.add(camera.position, 'y', -500, 500, 0.01);
    camGui.add(camera.position, 'z', -500, 500, 0.01);
    camGui.add(camera.rotation, 'x', -Math.PI, Math.PI, 0.01);
    camGui.add(camera.rotation, 'y', -Math.PI, Math.PI, 0.01);
    camGui.add(camera.rotation, 'z', -Math.PI, Math.PI, 0.01);

    camGui.open();
    const gui = new GUI();

    gui.add(effectController, 'enabled').onChange(matChanger);
    gui.add(effectController, 'jsDepthCalculation').onChange(matChanger);
    gui.add(effectController, 'shaderFocus').onChange(matChanger);
    gui.add(effectController, 'focalDepth', 0.0, 22.0).listen().onChange(matChanger);

    gui.add(effectController, 'fstop', 0.05, 50, 0.001).onChange(matChanger);
    gui.add(effectController, 'maxblur', 0.0, 5.0, 0.025).onChange(matChanger);

    gui.add(effectController, 'showFocus').onChange(matChanger);
    gui.add(effectController, 'manualdof').onChange(matChanger);
    gui.add(effectController, 'vignetting').onChange(matChanger);

    gui.add(effectController, 'depthblur').onChange(matChanger);

    gui.add(effectController, 'threshold', 0, 1, 0.001).onChange(matChanger);
    gui.add(effectController, 'gain', 0, 100, 0.001).onChange(matChanger);
    gui.add(effectController, 'bias', 0, 3, 0.001).onChange(matChanger);
    gui.add(effectController, 'fringe', 0, 5, 0.001).onChange(matChanger);

    gui.add(effectController, 'focalLength', 16, 80, 0.001).onChange(matChanger);

    gui.add(effectController, 'noise').onChange(matChanger);

    gui.add(effectController, 'dithering', 0, 0.001, 0.0001).onChange(matChanger);

    gui.add(effectController, 'pentagon').onChange(matChanger);

    gui.add(shaderSettings, 'rings', 1, 8).step(1).onChange(shaderUpdate);
    gui.add(shaderSettings, 'samples', 1, 13).step(1).onChange(shaderUpdate);

    gui.close();

    matChanger();

    function shaderUpdate() {

        postprocessing.materialBokeh.defines.RINGS = shaderSettings.rings;
        postprocessing.materialBokeh.defines.SAMPLES = shaderSettings.samples;
        postprocessing.materialBokeh.needsUpdate = true;

    }

    function matChanger() {
        for (const e in effectController) {

            if (e in postprocessing.bokeh_uniforms) {

                postprocessing.bokeh_uniforms[e].value = effectController[e];

            }

        }

        postprocessing.enabled = effectController.enabled;
        postprocessing.bokeh_uniforms['znear'].value = camera.near;
        postprocessing.bokeh_uniforms['zfar'].value = camera.far;
        camera.setFocalLength(effectController.focalLength);
    };
}
function postProcessOnResize(e) {

    const elWidth = el.clientWidth;
    const elHeight = el.clientHeight;

    camera.aspect = elWidth / elHeight;
    camera.updateProjectionMatrix();

    postprocessing.rtTextureDepth.setSize(elWidth, elHeight);
    postprocessing.rtTextureColor.setSize(elWidth, elHeight);

    postprocessing.bokeh_uniforms['textureWidth'].value = elWidth;
    postprocessing.bokeh_uniforms['textureHeight'].value = elHeight;

    renderer.setSize(elWidth, elHeight);
    postprocessing.composer.setSize(elWidth, elHeight);

}
function initPostProcesses() {
    const el = document.querySelector('mainCanvas');
    const elWidth = el.clientWidth;
    const elHeight = el.clientHeight;

    postprocessing.scene = new THREE.Scene();

    postprocessing.camera = new THREE.OrthographicCamera(window.innerWidth / - 2, window.innerWidth / 2, window.innerHeight / 2, window.innerHeight / - 2, - 10000, 10000);
    postprocessing.camera.position.z = 100;

    postprocessing.scene.add(postprocessing.camera);

    postprocessing.rtTextureDepth = new THREE.WebGLRenderTarget(elWidth, elHeight, { type: THREE.HalfFloatType });
    postprocessing.rtTextureColor = new THREE.WebGLRenderTarget(elWidth, elHeight, { type: THREE.HalfFloatType });

    const bokeh_shader = BokehShader;

    postprocessing.bokeh_uniforms = THREE.UniformsUtils.clone(bokeh_shader.uniforms);

    postprocessing.bokeh_uniforms['tColor'].value = postprocessing.rtTextureColor.texture;
    postprocessing.bokeh_uniforms['tDepth'].value = postprocessing.rtTextureDepth.texture;
    postprocessing.bokeh_uniforms['textureWidth'].value = elWidth;
    postprocessing.bokeh_uniforms['textureHeight'].value = elHeight;

    postprocessing.materialBokeh = new THREE.ShaderMaterial({

        uniforms: postprocessing.bokeh_uniforms,
        vertexShader: bokeh_shader.vertexShader,
        fragmentShader: bokeh_shader.fragmentShader,
        defines: {
            RINGS: shaderSettings.rings,
            SAMPLES: shaderSettings.samples
        }

    });

    postprocessing.quad = new THREE.Mesh(new THREE.PlaneGeometry(window.innerWidth, window.innerHeight), postprocessing.materialBokeh);
    postprocessing.quad.position.z = - 500;
    postprocessing.scene.add(postprocessing.quad);
}
 */